/**
 * Local phone length rules from org settings, with country-specific floors.
 * Saudi (`966` / `+966`) mobiles are 9 digits — never cap below that even if
 * admin `allowed_digit_for_phone` is lower (e.g. 8).
 */

import { toAsciiDigits } from '../../../shared/utils/apiParams';

const SAUDI_DIALING_DIGITS = '966';
const SAUDI_LOCAL_PHONE_DIGITS = 9;
const DEFAULT_PHONE_MAX_DIGITS = 8;

function normalizeDialingDigits(dialing_code: string | number | null | undefined): string {
  return extractAsciiPhoneDigits(String(dialing_code ?? ''));
}

/** ASCII `0-9` only, after converting Arabic-Indic / Persian digits (RTL keyboards). */
export function extractAsciiPhoneDigits(value: string | undefined | null): string {
  return toAsciiDigits(value).replace(/\D/g, '');
}

export function isSaudiDialingCode(dialing_code: string | number | null | undefined): boolean {
  const digits = normalizeDialingDigits(dialing_code);
  return digits === SAUDI_DIALING_DIGITS || digits === `00${SAUDI_DIALING_DIGITS}`;
}

/**
 * Max local digits for phone inputs (login / register).
 * Uses org `allowedDigitForPhone`, default 8; Saudi dialing code floors at 9.
 */
export function resolvePhoneMaxDigits(
  allowed_digit_for_phone: number | null | undefined,
  dialing_code: string | number | null | undefined,
  fallback: number = DEFAULT_PHONE_MAX_DIGITS,
): number {
  const configured =
    typeof allowed_digit_for_phone === 'number' && Number.isFinite(allowed_digit_for_phone)
      ? Math.max(1, Math.floor(allowed_digit_for_phone))
      : fallback;
  if (isSaudiDialingCode(dialing_code)) {
    return Math.max(configured, SAUDI_LOCAL_PHONE_DIGITS);
  }
  return configured;
}
